import { AMAZON_PRODUCT_ENRICHMENT } from '@/data/amazon-product-enrichment'
import { AFFILIATE_CATALOG } from '@/data/affiliate-product-catalog'
import { ARTICLE_PRODUCT_PICKS } from '@/data/article-product-picks'
import { extractAsin } from '@/lib/product-picks/resolve'

const GENERIC_AFFILIATE_ANCHORS = new Set([
  '',
  'view on amazon',
  'check price on amazon',
  'check on amazon',
  'buy on amazon',
  'see on amazon',
  'see price on amazon',
  'shop now on amazon',
  'buy now on amazon',
  'view price on amazon',
  'check price',
  'see price',
  'shop now',
  'buy now',
  'view price',
])

const GENERIC_AMAZON_SUFFIX_PREFIXES = new Set([
  'view',
  'check',
  'check price',
  'buy',
  'see',
  'see price',
  'shop',
  'shop now',
  'buy now',
  'view price',
])

let asinToProductName: Map<string, string> | null = null

function trimAnchorText(anchorText: string): string {
  return anchorText.trim()
}

function canonicalizeAnchorText(anchorText: string): string {
  return anchorText.trim().replace(/\s+/g, ' ').toLowerCase()
}

function addAsinName(map: Map<string, string>, asin: string | null, name: string): void {
  const normalizedName = name.trim()
  if (!asin || !normalizedName || map.has(asin)) return
  map.set(asin, normalizedName)
}

function getAsinToProductName(): Map<string, string> {
  if (asinToProductName) return asinToProductName

  const map = new Map<string, string>()

  for (const [asin, product] of Object.entries(AMAZON_PRODUCT_ENRICHMENT)) {
    addAsinName(map, asin.toUpperCase(), product.name)
  }

  for (const product of AFFILIATE_CATALOG) {
    addAsinName(map, extractAsin(product.affiliateUrl), product.displayName)
  }

  for (const picks of Object.values(ARTICLE_PRODUCT_PICKS)) {
    for (const product of picks) {
      addAsinName(map, extractAsin(product.affiliateUrl), product.name)
    }
  }

  asinToProductName = map
  return asinToProductName
}

function stripAmazonSuffixFromProductName(anchorText: string): string | null {
  const match = anchorText.match(/^(.*?)\s+on amazon$/i)
  if (!match) return null

  const prefix = match[1].trim()
  if (!prefix) return null

  const normalizedPrefix = canonicalizeAnchorText(prefix)
  if (GENERIC_AMAZON_SUFFIX_PREFIXES.has(normalizedPrefix)) return null

  return prefix
}

export function resolveAffiliateProductName(anchorText: string, href: string): string {
  const normalizedAnchorText = trimAnchorText(anchorText)
  const productNameWithoutSuffix = stripAmazonSuffixFromProductName(normalizedAnchorText)
  if (productNameWithoutSuffix) return productNameWithoutSuffix

  if (!GENERIC_AFFILIATE_ANCHORS.has(canonicalizeAnchorText(normalizedAnchorText))) {
    return normalizedAnchorText
  }

  const asin = extractAsin(href)
  if (!asin) return normalizedAnchorText

  return getAsinToProductName().get(asin) ?? `ASIN ${asin}`
}
